// PATH: src/hooks/useTabParam.ts — onglet actif porté par l'URL (?tab=).
//
// Sans cela, recharger une page d'onglets ramenait toujours au premier, et il
// était impossible d'envoyer à un collègue le lien de l'onglet qu'on regarde.
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useTabParam — [onglet actif, sélecteur], synchronisés avec « ?tab= ».
 *
 * L'onglet par défaut n'écrit RIEN dans l'URL : l'adresse d'une page reste
 * propre tant qu'on n'a pas changé d'onglet.
 *
 * Navigation en `replace` : changer d'onglet n'empile pas d'entrée d'historique.
 * Le bouton Précédent ramène donc à l'écran précédent, et non au parcours des
 * onglets qu'on vient de feuilleter.
 */
export const useTabParam = (defaultKey: string): [string, (key: string) => void] => {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || defaultKey;

  const setActive = useCallback(
    (key: string) => {
      // Fonction de mise à jour : les autres paramètres de l'URL (filtres d'un
      // écran, identifiant d'impression…) sont préservés tels quels.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key === defaultKey) next.delete("tab");
          else next.set("tab", key);
          return next;
        },
        { replace: true }
      );
    },
    [setParams, defaultKey]
  );

  return [active, setActive];
};
